import { stretchDrumLoop, type DrumStretchInput } from './drumStretch';

export interface DrumStretchWorkerRequest {
  id: number;
  input: DrumStretchInput;
}

export type DrumStretchWorkerResponse =
  | { id: number; channels: Float32Array[] }
  | { id: number; error: string };

interface RenderJob {
  id: number;
  key: string;
  input: DrumStretchInput;
  promise: Promise<Float32Array[] | null>;
  resolve: (channels: Float32Array[] | null) => void;
  reject: (error: Error) => void;
  cancelled: boolean;
}

const cancelled = new Error('Drum render cancelled');

/**
 * Owns one DSP worker and its queue. The player owns the rendered-buffer cache;
 * this layer only keeps work that still belongs to the requested tempos.
 * Cancellation resolves null, whereas a real render failure rejects so the
 * player can remember it without repeatedly retrying every beat.
 */
export class DrumStretchRenderer {
  private worker: Worker | null = null;
  private workerUnavailable = false;
  private active: RenderJob | null = null;
  private jobs = new Map<string, RenderJob>();
  private queue: RenderJob[] = [];
  private retained: Map<string, number> | null = null;
  private nextId = 0;
  private disposed = false;

  /** Before the first retain call, explicit renders are accepted unrestricted. */
  render(key: string, input: DrumStretchInput): Promise<Float32Array[] | null> {
    if (this.disposed || (this.retained && !this.retained.has(key))) return Promise.resolve(null);
    const existing = this.jobs.get(key);
    if (existing) return existing.promise;
    let resolve!: RenderJob['resolve'];
    let reject!: RenderJob['reject'];
    const promise = new Promise<Float32Array[] | null>((resolveJob, rejectJob) => {
      resolve = resolveJob;
      reject = rejectJob;
    });
    const job: RenderJob = {
      id: ++this.nextId,
      key,
      input,
      promise,
      resolve,
      reject,
      cancelled: false,
    };
    this.jobs.set(key, job);
    this.queue.push(job);
    this.pump();
    return promise;
  }

  /** Set order is priority: audible tempo first, then future-pass preparation. */
  retain(keys: ReadonlySet<string>): void {
    if (this.disposed) return;
    this.retained = new Map([...keys].map((key, index) => [key, index]));
    for (const job of this.jobs.values()) {
      if (!keys.has(job.key)) this.cancel(job);
    }
    this.queue = this.queue.filter((job) => !job.cancelled);
    this.pump();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const job of this.jobs.values()) this.cancel(job);
    this.queue = [];
    this.worker?.terminate();
    this.worker = null;
  }

  private cancel(job: RenderJob): void {
    job.cancelled = true;
    job.resolve(null);
    if (this.jobs.get(job.key) === job) this.jobs.delete(job.key);
    if (this.active === job && this.worker) {
      // Termination stops obsolete DSP immediately, including its allocations.
      // A new worker is cheaper than finishing every intermediate slider value.
      this.worker.terminate();
      this.worker = null;
      this.active = null;
    }
    // A fallback render relinquishes active only after its cooperative abort;
    // starting another here would put concurrent DSP back on the UI thread.
  }

  private pump(): void {
    if (this.disposed || this.active) return;
    if (this.retained) {
      this.queue.sort((a, b) => this.retained!.get(a.key)! - this.retained!.get(b.key)!);
    }
    const job = this.queue.shift();
    if (!job) return;
    this.active = job;
    const worker = this.getWorker();
    if (!worker) {
      void this.renderFallback(job);
      return;
    }
    try {
      // AudioBuffer channel data is shared by native playback and other tempos.
      // Only the active job receives copies; queued work allocates no extra PCM.
      const channels = job.input.channels.map((channel) =>
        channel.slice(0, job.input.sourceFrames),
      );
      const request: DrumStretchWorkerRequest = { id: job.id, input: { ...job.input, channels } };
      worker.postMessage(
        request,
        channels.map((channel) => channel.buffer as ArrayBuffer),
      );
    } catch (error) {
      this.finish(job, null, error instanceof Error ? error : new Error(String(error)));
    }
  }

  private getWorker(): Worker | null {
    if (this.worker) return this.worker;
    if (this.workerUnavailable || typeof Worker === 'undefined') return null;
    try {
      const worker = new Worker(new URL('./drumStretch.worker.ts', import.meta.url), {
        type: 'module',
      });
      worker.onmessage = (event: MessageEvent<DrumStretchWorkerResponse>) => {
        const job = this.active;
        if (this.worker !== worker || !job || event.data.id !== job.id) return;
        if ('error' in event.data) this.finish(job, null, new Error(event.data.error));
        else this.finish(job, event.data.channels);
      };
      worker.onerror = (event) => {
        if (this.worker !== worker) return;
        event.preventDefault();
        worker.terminate();
        this.worker = null;
        // A blocked/broken worker must not be recreated for every slider value.
        // Subsequent jobs use the cooperative path; this failure is reported.
        this.workerUnavailable = true;
        if (this.active)
          this.finish(this.active, null, new Error(event.message || 'Drum worker failed'));
      };
      worker.onmessageerror = () => {
        if (this.worker !== worker) return;
        worker.terminate();
        this.worker = null;
        this.workerUnavailable = true;
        if (this.active) this.finish(this.active, null, new Error('Unreadable drum worker result'));
      };
      this.worker = worker;
      return worker;
    } catch {
      // CSP restrictions, older browsers and tests can lack module workers.
      this.workerUnavailable = true;
      return null;
    }
  }

  private async renderFallback(job: RenderJob): Promise<void> {
    try {
      const channels = await stretchDrumLoop(job.input, async () => {
        if (job.cancelled || this.disposed) throw cancelled;
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        if (job.cancelled || this.disposed) throw cancelled;
      });
      this.finish(job, job.cancelled ? null : channels);
    } catch (error) {
      this.finish(
        job,
        null,
        error === cancelled || job.cancelled
          ? undefined
          : error instanceof Error
            ? error
            : new Error(String(error)),
      );
    }
  }

  private finish(job: RenderJob, channels: Float32Array[] | null, error?: Error): void {
    if (this.jobs.get(job.key) === job) this.jobs.delete(job.key);
    if (!job.cancelled) {
      if (error) job.reject(error);
      else job.resolve(channels);
    }
    if (this.active === job) this.active = null;
    this.pump();
  }
}
